import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { processMessagesListData } from './notificationUtils';
import { getRelativeTime } from '@/util/UtilFunctions';
import { NavDropdown } from './NavDropdown';

interface NavNotificationsProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  messages: unknown[] | null;
}

interface ProcessedMessage {
  projectId: string;
  title: string;
  latestMessage: string;
  unreadMessagesCount: number;
  msgCount: number;
  unreadTaskMsgsCount: number;
  taskMsgCount: number;
}

export function NavNotifications({
  open,
  onToggle,
  onClose,
  messages,
}: NavNotificationsProps) {
  const navigate = useNavigate();

  const unreadCount = useMemo(() => {
    if (!messages) return 0;
    return (messages as Array<{ read: { N: string } }>).filter(
      (m) => m.read?.N === '0',
    ).length;
  }, [messages]);

  const processedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    return processMessagesListData(messages) as ProcessedMessage[];
  }, [messages]);

  const handleNavigate = (projectId: string) => {
    navigate(`/in/project/${projectId}`);
    onClose();
  };

  return (
    <NavDropdown
      open={open}
      onClose={onClose}
      onTriggerClick={onToggle}
      align="right"
      className="w-80"
      trigger={
        <div className="relative">
          <Bell className="h-4 w-4 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-primary-foreground">
              {unreadCount > 99 ? '99' : unreadCount}
            </span>
          )}
        </div>
      }
    >
      <div className="border-b border-border-light px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notifications
        </span>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {!messages && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            Could not fetch messages
          </div>
        )}
        {messages && processedMessages.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            No messages
          </div>
        )}
        {processedMessages.map((msg) => {
          const relativeTime = getRelativeTime(
            new Date(),
            new Date(msg.latestMessage),
          );
          return (
            <button
              key={msg.projectId}
              type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-secondary"
              onClick={() => handleNavigate(msg.projectId)}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {msg.title}
                </p>
                <p className="text-xs text-muted-foreground">{relativeTime}</p>
              </div>
              <div className="flex flex-shrink-0 gap-1">
                {msg.unreadMessagesCount > 0 && (
                  <span className="rounded bg-primary-light px-1.5 py-0.5 text-[10px] font-medium text-primary-hover">
                    M {msg.unreadMessagesCount}
                  </span>
                )}
                {msg.unreadTaskMsgsCount > 0 && (
                  <span className="rounded bg-info-light px-1.5 py-0.5 text-[10px] font-medium text-info">
                    T {msg.unreadTaskMsgsCount}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </NavDropdown>
  );
}
